import { useState, useEffect, useRef } from "react";
import { TableRow, TableCell, Box, Typography, Button } from "@mui/material";

import {
  selectTransfersFetching,
  selectUnprocessedPayouts,
} from "@pd/layouts/MktplaceDashboard/redux/selectors/transfers";
import DynamicTableHOC from "@pd/components/DynamicTable";
import da from "@pd/layouts/MktplaceDashboard/redux/actions";
import { useAppDispatch, useAppSelector } from "@pd/redux/store";
import formatCurrency from "@pd/utils/formatCurrency";
import formatTimes from "@pd/utils/formatTimes";
import CopyIdButton from "@pd/components/CopyIdButton";

import { unprocessedPayoutsColumns } from "./tableSchemas";

export default function UnprocessedPayouts() {
  const dispatch = useAppDispatch();
  const apiFetching = useAppSelector(selectTransfersFetching);
  const rows = useAppSelector(selectUnprocessedPayouts);

  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const prevFetchingRef = useRef(false);

  function handleRefresh() {
    dispatch(da.transfers.getUnprocessedPayouts());
  }

  useEffect(() => {
    dispatch(da.transfers.getUnprocessedPayouts());
  }, []);

  useEffect(() => {
    if (prevFetchingRef.current && !apiFetching) {
      setLastUpdated(new Date());
      setSecondsAgo(0);
    }
    prevFetchingRef.current = apiFetching;
  }, [apiFetching]);

  useEffect(() => {
    if (!lastUpdated) return undefined;
    const interval = setInterval(() => {
      setSecondsAgo(Math.floor((Date.now() - lastUpdated.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [lastUpdated]);

  return (
    <Box sx={{ px: "50px", pt: "100px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontFamily: "CircularBold" }}>
            Unprocessed Payouts
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              color: "text.secondary",
              fontFamily: "CircularRegular",
            }}
          >
            {rows.length} unprocessed payout{rows.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {lastUpdated && (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontFamily: "CircularRegular" }}
            >
              Last updated{" "}
              {secondsAgo < 60
                ? "seconds ago"
                : `${Math.floor(secondsAgo / 60)}m ago`}
            </Typography>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={handleRefresh}
            disabled={apiFetching}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      <DynamicTableHOC
        columnDefs={unprocessedPayoutsColumns}
        onHeaderSort={() => {}}
        order="asc"
        orderBy="createdAt"
        tableSrc="unprocessedPayouts"
        showLoading={apiFetching}
        autoHidePagination
      >
        {rows.map((row) => (
          <TableRow
            key={row.transferId}
            sx={{
              td: {
                fontFamily: "CircularRegular",
                fontSize: "14px",
                width: 150,
                overflowX: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableCell sx={{ p: 0 }}>
              <CopyIdButton id={row.payoutId} />
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <CopyIdButton id={row.transferId} />
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <CopyIdButton id={row.vendorId} />
            </TableCell>
            <TableCell>{formatCurrency(row.amount)}</TableCell>
            <TableCell>{formatTimes(row.createdAt)}</TableCell>
            <TableCell>{row.vendorName}</TableCell>
          </TableRow>
        ))}
      </DynamicTableHOC>
    </Box>
  );
}
